import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ArrowLeft, ChevronRight, Copy, Headphones, HeadphoneOff, LogOut, Mic, MicOff, Pencil, Plus, Settings, SmilePlus, X } from 'lucide-react';
import type { ChosenStatus, PresenceStatus } from '@spawnwire/shared';
import { Avatar, Button, clsx, IconButton, Input, menuContent, menuItem, ProfileBanner, StatusDot } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt } from '../../lib/data';
import { disconnectGateway } from '../../lib/gateway';
import { copyText, useIsMobile } from '../../lib/hooks';
import { native } from '../../lib/native';
import { NameText } from '../../lib/nameFonts';
import { leaveVoice, toggleDeafen, toggleMute, useVoice } from '../../lib/voice';
import { actions, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { AboutMe, ProfileName } from '../members/Profile';
import { MicMenu } from './MicMenu';

const STATUSES: { value: ChosenStatus; label: string; description: string }[] = [
  { value: 'online', label: 'Online', description: '' },
  { value: 'idle', label: 'Idle', description: '' },
  { value: 'dnd', label: 'Do Not Disturb', description: 'You will not get desktop notifications or sounds.' },
  { value: 'invisible', label: 'Invisible', description: 'You appear offline but can use Spawnwire normally.' },
];

export async function logout(): Promise<void> {
  await leaveVoice();
  const revoked = await http.post('/auth/logout').then(
    () => true,
    () => false,
  );
  // In the Android app: forget the token, clear notifications, and if the server couldn't be told (offline),
  // end the session the next time it can be reached.
  await native?.signedOut(!revoked);
  disconnectGateway();
  actions.reset();
}

const setStatus = (status: ChosenStatus) => void attempt(() => http.patch('/users/@me/status', { status }));
const setCustomStatus = (customStatus: string) => attempt(() => http.patch('/users/@me/status', { customStatus }));

const row = 'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm text-fg hover:bg-hover';

/** The status row and its menu: the four statuses, then setting or clearing the custom status. */
function StatusMenu({ status, close, editCustom }: { status: PresenceStatus; close: () => void; editCustom: () => void }) {
  const me = useStore((s) => s.me);
  const isMobile = useIsMobile();
  if (!me) return null;
  const current = STATUSES.find((s) => s.value === me.status) ?? STATUSES[0]!;
  return (
    <DropdownPrimitive.Root modal={false}>
      <DropdownPrimitive.Trigger asChild>
        <button type="button" className={clsx(row, 'data-[state=open]:bg-hover')}>
          <StatusDot status={status} className="size-2.5" ring="ring-transparent" />
          <span className="flex-1">{current.label}</span>
          <ChevronRight className="size-4 text-muted" aria-hidden />
        </button>
      </DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        {/* Opens to the side like a submenu; on a phone there is no room beside the card, so it opens above the row. */}
        <DropdownPrimitive.Content
          className={clsx(menuContent, 'w-64')}
          side={isMobile ? 'top' : 'right'}
          align={isMobile ? 'end' : 'start'}
          sideOffset={isMobile ? 4 : 18}
          collisionPadding={8}
        >
          <DropdownPrimitive.RadioGroup value={me.status} onValueChange={(v) => setStatus(v as ChosenStatus)}>
            {STATUSES.map((s) => (
              <DropdownPrimitive.RadioItem key={s.value} value={s.value} className={clsx(menuItem, 'group items-start py-2')} onSelect={close}>
                <StatusDot status={s.value === 'invisible' ? 'offline' : s.value} className="mt-1 size-2.5 shrink-0" ring="ring-transparent" />
                <span className="flex-1">
                  <span className="block">{s.label}</span>
                  {s.description && <span className="block text-xs opacity-70">{s.description}</span>}
                </span>
                <DropdownPrimitive.ItemIndicator className="mt-1 size-2 shrink-0 rounded-full bg-current" />
              </DropdownPrimitive.RadioItem>
            ))}
          </DropdownPrimitive.RadioGroup>
          <DropdownPrimitive.Separator className="my-1 h-px bg-line" />
          <DropdownPrimitive.Item className={menuItem} onSelect={editCustom}>
            <SmilePlus className="size-4 shrink-0" aria-hidden />
            {me.customStatus ? 'Edit Custom Status' : 'Set Custom Status'}
          </DropdownPrimitive.Item>
          {me.customStatus && (
            <DropdownPrimitive.Item
              className={menuItem}
              onSelect={() => {
                void setCustomStatus('');
                close();
              }}
            >
              <X className="size-4 shrink-0" aria-hidden />
              Clear Custom Status
            </DropdownPrimitive.Item>
          )}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}

/** Your profile as others see it, with shortcuts underneath: the popout above the account card. */
function SelfPopout({ status, close, editCustom }: { status: PresenceStatus; close: () => void; editCustom: () => void }) {
  const me = useStore((s) => s.me);
  if (!me) return null;
  return (
    <>
      <ProfileBanner user={me} className={me.bannerUrl ? 'h-[100px]' : 'h-[72px]'} />
      <div className="px-4 pb-4">
        <div className="-mt-9 flex items-start gap-3">
          <span className="shrink-0 rounded-full ring-[6px] ring-sidebar">
            <Avatar user={me} size={72} status={status} ringClass="ring-sidebar" />
          </span>
          {/* The custom status as a thought bubble beside the avatar; clicking it edits it. */}
          <button
            type="button"
            onClick={editCustom}
            aria-label={me.customStatus ? `Custom status: ${me.customStatus}. Edit` : 'Set a custom status'}
            className={clsx(
              'relative mt-4 min-w-0 rounded-2xl border border-line bg-overlay px-3 py-1.5 text-left text-xs shadow-pop hover:bg-elevated',
              me.customStatus ? 'text-fg' : 'text-muted',
            )}
          >
            <span className="absolute -left-1.5 top-1 size-2.5 rounded-full border border-line bg-overlay" aria-hidden />
            {me.customStatus ? (
              <span className="line-clamp-2 [overflow-wrap:anywhere]">{me.customStatus}</span>
            ) : (
              <span className="flex items-center gap-1">
                <Plus className="size-3.5" aria-hidden /> Add Status
              </span>
            )}
          </button>
        </div>
        <div className="mt-3">
          <ProfileName user={me} />
        </div>
        {me.bio && <AboutMe bio={me.bio} clamp className="mt-3" />}

        <div className="mt-3 rounded-lg bg-main p-1.5">
          <button
            type="button"
            className={row}
            onClick={() => {
              close();
              ui.open({ kind: 'userSettings', section: 'profile' });
            }}
          >
            <Pencil className="size-4 text-muted" aria-hidden /> Edit Profile
          </button>
          <div className="mx-2.5 my-1 h-px bg-line" />
          <StatusMenu status={status} close={close} editCustom={editCustom} />
        </div>
        <div className="mt-2 rounded-lg bg-main p-1.5">
          <button
            type="button"
            className={row}
            onClick={() => {
              void copyText(me.id).then(
                () => ui.toast('User ID copied.', 'success'),
                () => ui.toast('Could not copy your user ID.', 'error'),
              );
              close();
            }}
          >
            <Copy className="size-4 text-muted" aria-hidden /> Copy User ID
          </button>
          <div className="mx-2.5 my-1 h-px bg-line" />
          <button type="button" className={clsx(row, 'text-danger hover:bg-danger-soft')} onClick={() => void logout()}>
            <LogOut className="size-4" aria-hidden /> Log Out
          </button>
        </div>
      </div>
    </>
  );
}

function CustomStatusForm({ back, close }: { back: () => void; close: () => void }) {
  const current = useStore((s) => s.me?.customStatus ?? '');
  const [text, setText] = useState(current);
  return (
    <form
      className="p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void setCustomStatus(text.trim());
        close();
      }}
    >
      <div className="flex items-center gap-2">
        <IconButton icon={ArrowLeft} label="Back" size="sm" onClick={back} />
        <h2 className="text-base font-semibold text-fg">Custom status</h2>
      </div>
      <Input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What's on your mind?"
        maxLength={128}
        aria-label="Custom status"
        className="mt-3"
      />
      <p className="mt-1.5 text-xs text-muted">Shown on your profile and under your name. Emoji welcome.</p>
      <div className="mt-4 flex justify-end gap-2">
        {current && (
          <Button
            variant="ghost"
            size="sm"
            className="mr-auto"
            onClick={() => {
              void setCustomStatus('');
              close();
            }}
          >
            Clear
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={back}>
          Cancel
        </Button>
        <Button type="submit" size="sm">
          Save
        </Button>
      </div>
    </form>
  );
}

export function UserPanel() {
  const me = useStore((s) => s.me);
  const presence = useStore((s) => (s.me ? (s.presences[s.me.id] ?? 'online') : 'offline'));
  const muted = useVoice((v) => v.muted);
  const deafened = useVoice((v) => v.deafened);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const { pathname } = useLocation();
  // Following a link out of the popout (e.g. Go to Server on your tag) closes it.
  useEffect(() => setOpen(false), [pathname]);
  if (!me) return null;
  const shown = me.status === 'invisible' ? 'offline' : me.status === 'online' ? presence : me.status;
  const close = () => setOpen(false);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setEditing(false);
      }}
    >
      {/* The popout lines up with the account card's left edge, not just the name button. */}
      <Popover.Anchor asChild>
        <div className="flex h-14 shrink-0 items-center gap-1 px-2">
          <Popover.Trigger asChild>
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-hover data-[state=open]:bg-hover" aria-label="Your profile and status">
              <Avatar user={me} size={32} status={shown} ringClass="ring-elevated" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-fg">
                  <NameText font={me.nameFont}>{me.displayName}</NameText>
                </span>
                <span className="block truncate text-xs text-muted">{me.customStatus || `@${me.username}`}</span>
              </span>
            </button>
          </Popover.Trigger>
          <IconButton icon={muted ? MicOff : Mic} label={muted ? 'Unmute' : 'Mute'} danger={muted} onClick={toggleMute} />
          <MicMenu />
          <IconButton icon={deafened ? HeadphoneOff : Headphones} label={deafened ? 'Undeafen' : 'Deafen'} danger={deafened} onClick={toggleDeafen} />
          <IconButton icon={Settings} label="User settings" onClick={() => ui.open({ kind: 'userSettings' })} />
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={10}
          collisionPadding={8}
          aria-label="Your profile"
          className="animate-pop scroll-thin z-[70] max-h-[var(--radix-popover-content-available-height)] w-[calc(100vw-16px)] overflow-y-auto overflow-x-hidden rounded-xl border border-line bg-sidebar shadow-pop outline-none sm:w-[300px]"
        >
          {editing ? <CustomStatusForm back={() => setEditing(false)} close={close} /> : <SelfPopout status={shown} close={close} editCustom={() => setEditing(true)} />}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
